import type { ReactNode } from 'react';
import { ChevronUp, ChevronDown, Loader2, ArrowUpRight, CalendarDays, FileSpreadsheet } from 'lucide-react';
import { Transaction, SortField, SortDirection } from '../../types/dashboard';
import TransactionActions from './TransactionActions';

interface TransactionListProps {
  transactions: Transaction[];
  loading: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  isAdmin: boolean;
  onSort: (field: SortField) => void;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  onValidate: (transaction: Transaction) => void;
  onModificationRequest: (transaction: Transaction) => void;
  onCancel: (transaction: Transaction) => void;
  onCompromise: (transaction: Transaction, compromiseDate: string, expectedActeDate: string) => void;
  onViewTransaction?: (transaction: Transaction) => void;
  onActeTransaction?: (transaction: Transaction) => void;
  onEncaissementTransaction?: (transaction: Transaction) => void;
  onClotureTransaction?: (transaction: Transaction) => void;
  onRegulTransaction?: (transaction: Transaction) => void;
}

const STATUS_CONFIG: Record<string, { label: string; dot: string; text: string; bg: string; border: string }> = {
  potentiel: { label: 'Potentiel', dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200'  },
  compromis: { label: 'Compromis', dot: 'bg-blue-400',    text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200'   },
  acte:      { label: 'Acté',      dot: 'bg-violet-400',  text: 'text-violet-700',  bg: 'bg-violet-50',  border: 'border-violet-200' },
  encaisse:  { label: 'Encaissé',  dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200'},
  cloture:   { label: 'Clôturé',   dot: 'bg-slate-400',   text: 'text-slate-500',   bg: 'bg-slate-100',  border: 'border-slate-200'  },
  annule:    { label: 'Annulé',    dot: 'bg-red-400',     text: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200'    },
};

const ROLE_CONFIG: Record<string, { label: string; pill: string; tag: string }> = {
  vendeur:           { label: 'V',   pill: 'bg-blue-50   border-blue-100   text-blue-700',   tag: 'text-blue-400'   },
  apporteur:         { label: 'A',   pill: 'bg-emerald-50 border-emerald-100 text-emerald-700', tag: 'text-emerald-400' },
  vendeur_apporteur: { label: 'V/A', pill: 'bg-violet-50 border-violet-100 text-violet-700', tag: 'text-violet-400' },
  reco:              { label: 'R',   pill: 'bg-amber-50  border-amber-100  text-amber-700',  tag: 'text-amber-400'  },
  autre:             { label: 'X',   pill: 'bg-slate-100 border-slate-200  text-slate-600',  tag: 'text-slate-400'  },
};

export default function TransactionList({
  transactions,
  loading,
  sortField,
  sortDirection,
  isAdmin,
  onSort,
  onEdit,
  onDelete,
  onValidate,
  onModificationRequest: _onModificationRequest,
  onCancel,
  onCompromise,
  onViewTransaction,
  onActeTransaction,
  onEncaissementTransaction,
  onClotureTransaction,
  onRegulTransaction,
}: TransactionListProps) {

  const fmtAmount = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  const fmtDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  const calculateNetFees = (t: Transaction) => {
    const retro = t.retrocessions?.reduce((s, r) => s + r.amount, 0) ?? 0;
    return t.fees_without_tax - retro;
  };

  const COLUMNS: { field: SortField; label: string }[] = [
    { field: 'property_number',  label: 'N° Mandat'      },
    { field: 'compromise_date',  label: 'Date compromis' },
    { field: 'agency',           label: 'Agence'         },
    { field: 'negotiator',       label: 'Négociateur'    },
    { field: 'seller',           label: 'Parties'        },
    { field: 'sale_price',       label: 'Prix de vente'  },
    { field: 'net_fees',         label: 'Honoraires'     },
    { field: 'status',           label: 'Statut'         },
  ];

  /* ── composants locaux ── */
  function TH({ children, center, sortable, field }: {
    children: ReactNode; center?: boolean; sortable?: boolean; field?: SortField
  }) {
    const base = `px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap ${center ? 'text-center' : 'text-left'}`;
    if (!sortable || !field) return <th className={base}>{children}</th>;
    const active = field === sortField;
    return (
      <th className={`${base} cursor-pointer select-none hover:text-slate-600 transition-colors`} onClick={() => onSort(field)}>
        <div className={`inline-flex items-center gap-1 ${active ? 'text-indigo-500' : ''}`}>
          {children}
          {active
            ? (sortDirection === 'asc'
                ? <ChevronUp size={11} className="shrink-0" />
                : <ChevronDown size={11} className="shrink-0" />)
            : <span className="w-[11px]" />}
        </div>
      </th>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] border-collapse">

        {/* ── Headers ── */}
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/60">
            {COLUMNS.map(({ field, label }) => (
              <TH key={field} field={field} sortable>{label}</TH>
            ))}
            <TH center>Régul</TH>
            <TH>Actions</TH>
          </tr>
        </thead>

        {/* ── Body ── */}
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={10} className="px-5 py-16 text-center">
                <div className="flex flex-col items-center gap-2 text-slate-300">
                  <Loader2 size={22} className="animate-spin" />
                  <span className="text-sm">Chargement…</span>
                </div>
              </td>
            </tr>
          ) : transactions.length === 0 ? (
            <tr>
              <td colSpan={10} className="px-5 py-16 text-center text-sm text-slate-400">
                Aucune transaction trouvée
              </td>
            </tr>
          ) : (
            transactions.map((t, i) => {
              const isRegul  = !!t.regul;
              const status   = STATUS_CONFIG[t.status] ?? { label: t.status, dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' };
              const netFees  = calculateNetFees(t);
              const dateStr  = fmtDate(t.compromise_date);
              const isLast   = i === transactions.length - 1;

              return (
                <tr
                  key={t.id}
                  onClick={() => onViewTransaction?.(t)}
                  className={`cursor-pointer transition-colors ${isRegul ? 'bg-rose-50/40 hover:bg-rose-50/70' : 'hover:bg-slate-50/70'} ${!isLast ? 'border-b border-slate-100' : ''}`}
                >

                  {/* ── N° Mandat ── */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold">
                      {t.property_number}
                    </span>
                  </td>

                  {/* ── Date compromis ── */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    {dateStr ? (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${isRegul ? 'bg-rose-50 border-rose-200 text-rose-600' : 'bg-blue-50 border-blue-100 text-blue-600'}`}>
                        <CalendarDays size={11} className="shrink-0" />
                        {dateStr}
                      </span>
                    ) : (
                      <span className="text-slate-200 text-sm">—</span>
                    )}
                  </td>

                  {/* ── Agence ── */}
                  <td className="px-5 py-4 min-w-[110px]">
                    <div className="flex flex-col gap-1">
                      {t.agencies.length === 0
                        ? <span className="text-slate-300 text-sm">—</span>
                        : t.agencies.map((a, idx) => (
                            <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold w-fit">
                              {a}
                            </span>
                          ))
                      }
                    </div>
                  </td>

                  {/* ── Négociateurs ── */}
                  <td className="px-5 py-4 min-w-[140px] max-w-[200px]">
                    <div className="flex flex-col gap-1">
                      {t.negotiators.length === 0
                        ? <span className="text-slate-300 text-sm">—</span>
                        : t.negotiators.map((n, idx) => {
                            const rc = ROLE_CONFIG[n.role] ?? ROLE_CONFIG['autre'];
                            return (
                              <span key={idx} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium w-fit ${rc.pill}`}>
                                {n.name}
                                <span className={`font-semibold ${rc.tag}`}>{rc.label} {n.percentage}%</span>
                              </span>
                            );
                          })
                      }
                    </div>
                  </td>

                  {/* ── Parties (Vendeur + Acquéreur) ── */}
                  <td className="px-5 py-4 min-w-[150px] max-w-[210px]">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-baseline gap-1.5">
                        <span className="shrink-0 text-[10px] font-bold text-amber-500 uppercase tracking-wide">Vend.</span>
                        <span className="text-sm text-slate-700 leading-tight break-words">{t.seller || '—'}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="shrink-0 text-[10px] font-bold text-sky-500 uppercase tracking-wide">Acq.</span>
                        <span className="text-sm text-slate-700 leading-tight break-words">{t.buyer || '—'}</span>
                      </div>
                    </div>
                  </td>

                  {/* ── Prix de vente ── */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-sm font-semibold ${isRegul ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-amber-50 border-amber-100 text-amber-800'}`}>
                      {fmtAmount(t.sale_price)}
                    </span>
                  </td>

                  {/* ── Honoraires (Net principal + TTC/HT secondaires) ── */}
                  <td className="px-5 py-4 min-w-[165px] whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm font-bold w-fit ${isRegul ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                        <ArrowUpRight size={13} className="shrink-0" />
                        {fmtAmount(netFees)}
                      </span>
                      <div className="flex flex-col gap-0.5 pl-0.5">
                        <span className="text-[11px] text-slate-400">
                          <span className="font-medium text-slate-500">TTC</span> {fmtAmount(t.fees_with_tax)}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          <span className="font-medium text-slate-500">HT</span> {fmtAmount(t.fees_without_tax)}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* ── Statut ── */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${status.bg} ${status.text} ${status.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${status.dot}`} />
                      {status.label}
                    </span>
                  </td>

                  {/* ── Régul ── */}
                  <td className="px-5 py-4 text-center whitespace-nowrap">
                    {isRegul && (
                      <span
                        title={t.commentaire_regul || 'Régularisation en cours'}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-600 text-xs font-semibold"
                      >
                        <FileSpreadsheet size={11} />
                        Régul
                      </span>
                    )}
                  </td>

                  {/* ── Actions ── */}
                  <td className="px-5 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <TransactionActions
                      transaction={t}
                      isAdmin={isAdmin}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onValidate={onValidate}
                      onCancel={onCancel}
                      onCompromise={onCompromise}
                      onActeTransaction={onActeTransaction}
                      onEncaissementTransaction={onEncaissementTransaction}
                      onClotureTransaction={onClotureTransaction}
                      onRegulTransaction={onRegulTransaction}
                    />
                  </td>

                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
